import React from 'react';
import './App.css';
import * as Sentry from '@sentry/react';
import EditCase from './components/EditCase';
import { AuthProvider } from './context/AuthContext';
import { UploadProvider } from './context/UploadContext';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Login } from './components/Login';
import { Register } from './components/Register';
import { RequestAccess } from './components/RequestAccess';
import { ForgotPassword } from './components/ForgotPassword';
import { AcceptTos } from './components/AcceptTos';
import { UserProfile } from './components/UserProfile';
import { ProtectedRoute } from './components/ProtectedRoute';
import CaseManagement from './components/CaseManagement';
import MenuBar from './components/MenuBar';
import NewCase from './components/NewCase';
import LandingPage from './components/LandingPage';
import AdminDashboard from './components/AdminDashboard';
import WorkflowList from './components/workflows/WorkflowList';
import WorkflowEditor from './components/workflows/WorkflowEditor';
import FacilityDashboard from './components/facility/FacilityDashboard';
import FacilityTeam from './components/facility/FacilityTeam';
import BillingPage from './components/BillingPage';
import HelpPage from './components/HelpPage';
import FeedbackButton from './components/FeedbackButton';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { FAQ } from './components/FAQ';
import HomeIndex from './components/HomeIndex';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/**
 * Generic fallback for routes that handle patient data.
 * Uses a plain error boundary instead of Sentry.ErrorBoundary to prevent
 * component state (structuredInfo, HPI, diagnosis) from being sent to Sentry.
 */
function SafeFallback() {
  return <p>Something went wrong. Please refresh the page.</p>;
}

class PHISafeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // Report only the error message and type — no component state or stack
    Sentry.captureException(error, {
      contexts: { react: { componentStack: '[redacted — PHI-safe boundary]' } },
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <SafeFallback />;
    }
    return this.props.children;
  }
}

function App() {
  return (
    <Router>
      <AuthProvider>
      <UploadProvider>
        <div className="App">
          <ToastContainer />
          <Routes>
            {/* Public routes — safe for Sentry.ErrorBoundary (no PHI) */}
            <Route path="/login" element={<Sentry.ErrorBoundary fallback={<SafeFallback />}><Login /></Sentry.ErrorBoundary>} />
            <Route path="/register" element={<Sentry.ErrorBoundary fallback={<SafeFallback />}><Register /></Sentry.ErrorBoundary>} />
            <Route path="/request-access" element={<Sentry.ErrorBoundary fallback={<SafeFallback />}><RequestAccess /></Sentry.ErrorBoundary>} />
            <Route path="/forgot-password" element={<Sentry.ErrorBoundary fallback={<SafeFallback />}><ForgotPassword /></Sentry.ErrorBoundary>} />
            <Route path="/privacy" element={<Sentry.ErrorBoundary fallback={<SafeFallback />}><PrivacyPolicy /></Sentry.ErrorBoundary>} />
            <Route path="/faq" element={<Sentry.ErrorBoundary fallback={<SafeFallback />}><FAQ /></Sentry.ErrorBoundary>} />

            {/* Accept ToS - authenticated but no ToS requirement */}
            <Route
              path="/accept-tos"
              element={
                <ProtectedRoute requireTos={false}>
                  <Sentry.ErrorBoundary fallback={<SafeFallback />}>
                    <AcceptTos />
                  </Sentry.ErrorBoundary>
                </ProtectedRoute>
              }
            />

            {/* Home: role-based index (unauthenticated → landing; facility admin → facility dashboard; others → /cases) */}
            <Route path="/" element={<HomeIndex />} />

            {/* Case list — authenticated users land here by default */}
            <Route
              path="/cases"
              element={
                <ProtectedRoute>
                  <>
                    <MenuBar />
                    <div className="app-content">
                      <CaseManagement />
                    </div>
                  </>
                </ProtectedRoute>
              }
            />

            {/* Patient data routes — use PHISafeErrorBoundary */}
            <Route
              path="/case/new"
              element={
                <ProtectedRoute>
                  <PHISafeErrorBoundary>
                    <MenuBar />
                    <div className="app-content">
                      <NewCase />
                    </div>
                  </PHISafeErrorBoundary>
                </ProtectedRoute>
              }
            />

            <Route
              path="/case/:caseId"
              element={
                <ProtectedRoute>
                  <PHISafeErrorBoundary>
                    <MenuBar />
                    <div className="app-content">
                      <EditCase />
                    </div>
                  </PHISafeErrorBoundary>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Sentry.ErrorBoundary fallback={<SafeFallback />}>
                    <MenuBar />
                    <div className="app-content">
                      <AdminDashboard />
                    </div>
                  </Sentry.ErrorBoundary>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/workflows"
              element={
                <ProtectedRoute>
                  <Sentry.ErrorBoundary fallback={<SafeFallback />}>
                    <MenuBar />
                    <div className="app-content">
                      <WorkflowList />
                    </div>
                  </Sentry.ErrorBoundary>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/workflows/:configId"
              element={
                <ProtectedRoute>
                  <Sentry.ErrorBoundary fallback={<SafeFallback />}>
                    <MenuBar />
                    <div className="app-content">
                      <WorkflowEditor />
                    </div>
                  </Sentry.ErrorBoundary>
                </ProtectedRoute>
              }
            />

            <Route
              path="/facility/:facilityId/dashboard"
              element={
                <ProtectedRoute>
                  <Sentry.ErrorBoundary fallback={<SafeFallback />}>
                    <MenuBar />
                    <div className="app-content">
                      <FacilityDashboard />
                    </div>
                  </Sentry.ErrorBoundary>
                </ProtectedRoute>
              }
            />

            <Route
              path="/facility/:facilityId/team"
              element={
                <ProtectedRoute>
                  <Sentry.ErrorBoundary fallback={<SafeFallback />}>
                    <MenuBar />
                    <div className="app-content">
                      <FacilityTeam />
                    </div>
                  </Sentry.ErrorBoundary>
                </ProtectedRoute>
              }
            />

            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <Sentry.ErrorBoundary fallback={<SafeFallback />}>
                    <MenuBar />
                    <div className="app-content">
                      <BillingPage />
                    </div>
                  </Sentry.ErrorBoundary>
                </ProtectedRoute>
              }
            />

            <Route
              path="/help"
              element={
                <ProtectedRoute>
                  <Sentry.ErrorBoundary fallback={<SafeFallback />}>
                    <MenuBar />
                    <div className="app-content">
                      <HelpPage />
                    </div>
                  </Sentry.ErrorBoundary>
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Sentry.ErrorBoundary fallback={<SafeFallback />}>
                    <MenuBar />
                    <div className="app-content">
                      <UserProfile />
                    </div>
                  </Sentry.ErrorBoundary>
                </ProtectedRoute>
              }
            />

            {/* Redirect unknown routes to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <FeedbackButton />
        </div>
      </UploadProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
